import { useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { LossPercentageDto } from 'api/Clients/Types/LossPercentageDto'
import AddButton from 'components/AddButton/AddButton'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { calculatePageIndex } from 'features/DataGrid/helpers'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from 'utils/api/useFetchOneWithFetchingUpdate'
import { links } from 'utils/featureToggles'
import { getPaginationInitialState } from 'utils/paginationInitialState'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { disclaimerLossPercentageColumns } from './columns'
import {
  DisclaimerLossPercentageAddModal,
  DisclaimerLossPercentageEditModal,
  LossPercentageValues,
} from './DisclaimerLossPercentageModal'

const DisclaimerLossPercentagePage = (): JSX.Element => {
  const setToast = useContext(ToastContext)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const [disclaimerLossPercentagesData, setDisclaimerLossPercentagesData] = useState<
    LossPercentageDto[]
  >([])
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  const [addEntityModalOpen, setAddEntityModalOpen] = useState<{
    visible: boolean
    data?: LossPercentageDto
  }>({
    visible: false,
  })
  const [editEntityModalOpen, setEditEntityModalOpen] = useState<{
    visible: boolean
    data?: LossPercentageDto
  }>({
    visible: false,
  })
  const [auditLogModal, setAuditLogModal] = useState<{
    visible: boolean
    data?: LossPercentageDto
  }>({
    visible: false,
  })

  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    return clientsApiClient.getComplianceLossPercentages({
      pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
      pageSize: paginationState.take,
    })
  }, [])
  const { data, isLoading } = useFetchOneWithFetchingUpdate(callback)

  useEffect(() => {
    if (data) {
      setDisclaimerLossPercentagesData(data.items)
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState(event.page)
    forceRefresh()
  }

  const handleAddModalOpen = () => {
    setAddEntityModalOpen({ visible: true })
  }

  const handleEditModal = (disclaimerLossPercentage: LossPercentageDto) => {
    setEditEntityModalOpen({ data: disclaimerLossPercentage, visible: true })
  }

  const handleAddModalClose = () => {
    setAddEntityModalOpen({ visible: false })
  }

  const handleEditModalClose = () => {
    setEditEntityModalOpen({ visible: false })
  }

  const handleAddSubmit = async (disclaimerLossPercentage: LossPercentageValues) => {
    await clientsApiClient.addComplianceLossPercentages(disclaimerLossPercentage)
    setAddEntityModalOpen({ visible: false })
    setToast(successToast(`Disclaimer Loss Percentage added successfully.`))
    forceRefresh()
  }

  const handleUpdateSubmit = async (disclaimerLossPercentage: LossPercentageValues) => {
    if (disclaimerLossPercentage?.id) {
      await clientsApiClient.updateComplianceLossPercentages({
        id: disclaimerLossPercentage.id,
        percentage: disclaimerLossPercentage.percentage,
      })
      setEditEntityModalOpen({ visible: false })
      setToast(successToast(`Disclaimer Loss Percentage updated successfully.`))
      forceRefresh()
    }
  }

  const handleAuditLog = (disclaimerLossPercentage: LossPercentageDto) => {
    setAuditLogModal({ data: disclaimerLossPercentage, visible: true })
  }

  const handleAuditLogClose = () => {
    setAuditLogModal({ visible: false })
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <>
      {addEntityModalOpen.visible && (
        <DisclaimerLossPercentageAddModal
          data={addEntityModalOpen.data}
          visible={addEntityModalOpen.visible}
          onSubmit={handleAddSubmit}
          onClose={handleAddModalClose}
        />
      )}
      {editEntityModalOpen.visible && (
        <DisclaimerLossPercentageEditModal
          data={editEntityModalOpen.data}
          visible={editEntityModalOpen.visible}
          onSubmit={handleUpdateSubmit}
          onClose={handleEditModalClose}
        />
      )}
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={handleAuditLogClose}
          lossPercentageId={auditLogModal.data.id}
        />
      )}

      <Breadcrumbs
        crumbs={[{ title: 'Disclaimer Loss Percentage', link: links.disclaimerLossPercentage.url }]}
      />
      <Loading isLoading={isLoading}>
        <AddButton text='Add Entity' onClick={handleAddModalOpen} data-test='add-entity-button' />
        <Grid
          data={disclaimerLossPercentagesData}
          take={paginationState.take}
          skip={paginationState.skip}
          total={data?.totalResults || 0}
          onPageChange={handlePageChange}
          pageable={pageableSettings}
        >
          {disclaimerLossPercentageColumns
            .filter((c) => c.show !== false)
            .map((column, idx) => (
              <Column key={idx} {...column} />
            ))}
          <Column
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      'data-test': 'edit',
                      onClick: () => handleEditModal(gridCellProps.dataItem),
                    },
                    {
                      name: 'Audit Log',
                      'data-test': 'audit_log',
                      onClick: () => handleAuditLog(gridCellProps.dataItem),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        </Grid>
      </Loading>
    </>
  )
}

export default DisclaimerLossPercentagePage
